/**
 * Unread-count visual badge across three surfaces:
 *
 *   1. `document.title` prefix `(N) Reflecto` — visible in the tab
 *      strip even when the favicon overlay is invisible at small
 *      sizes.
 *   2. Favicon canvas overlay — replaces `<link rel="icon">` with a
 *      red-pill circle painted on top of the base PWA icon. Picked
 *      up by macOS, Windows, and Linux taskbar previews.
 *   3. `navigator.setAppBadge(N)` — system-level badge for installed
 *      PWAs (Chromium-only as of writing). No-ops gracefully on
 *      browsers without the API.
 *
 * The three are kept synchronous via a single `applyBadge(count)`
 * call. The MainPage (and any future surface that owns the unread
 * count) drives this from a `useEffect` watching the storage value
 * — no module-internal subscription, no risk of double-updates.
 *
 * Counts > 99 collapse to `99+` so the favicon overlay stays
 * legible. The page title still shows the full integer because tab-
 * strip width usually accommodates it and 999 is more informative
 * than `99+` when triaging which tab to focus.
 */

const BASE_TITLE = "Reflecto";
const FAVICON_SIZE = 64; // px; matches the base icon's natural size
/**
 * Hex fallback used when the design-token CSS variable is missing
 * — e.g. before `apps/web/src/global.css` has loaded the token sheets,
 * or in a test environment that doesn't import the full token CSS.
 * Mirrors the `--text-negative` light-theme value so the badge
 * still reads as a "danger / unread" red without the cascade.
 */
const BADGE_BG_FALLBACK = "#f55951";

/**
 * Read the live `--text-negative` design token from the document
 * root so the favicon overlay tracks the user's theme (the dark-theme
 * variant is a softer salmon than the light-theme value, both already
 * defined in `packages/client-ui/src/tokens/tokens-figma.css`). Falls
 * back to the hex above when the cascade isn't ready or the doc
 * isn't accessible (SSR-style builds, certain test environments).
 *
 * Read at paint time rather than module-init time so a theme switch
 * while the tab is open picks up the new color on the next favicon
 * repaint (which fires on every `applyBadge` call).
 */
function badgeColor(): string {
  if (typeof document === "undefined") return BADGE_BG_FALLBACK;
  const root = document.documentElement;
  // NOTE: a token name in a JS string is invisible to both the CSS cascade
  // and to scripts/check-token-refs.ts, which only scans .css files. If this
  // token is ever renamed again, nothing will fail — the badge will just
  // silently fall back to the hex above. Grep for the name when renaming.
  const fromToken = getComputedStyle(root).getPropertyValue("--text-negative").trim();
  return fromToken || BADGE_BG_FALLBACK;
}

let cachedBaseImage: HTMLImageElement | null = null;
let baseImageLoad: Promise<HTMLImageElement> | null = null;

/**
 * Monotonically increasing generation counter — incremented on every
 * `applyBadge(...)` call. Each in-flight `paintFavicon` captures the
 * generation at call time and bails before swapping the favicon link
 * if a newer call has already started. Without this guard a burst of
 * count changes could land out of order (the older paint's microtask
 * resolves after the newer one's, leaving a stale digit in the
 * favicon canvas) — rare in practice but real, and the fix is
 * essentially free.
 */
let badgeGeneration = 0;

/**
 * Load the base PWA icon once and cache the decoded HTMLImageElement.
 * Subsequent badge updates reuse the same image instead of re-fetching
 * + re-decoding on every count change.
 */
function loadBaseIcon(): Promise<HTMLImageElement> {
  if (cachedBaseImage) return Promise.resolve(cachedBaseImage);
  if (baseImageLoad) return baseImageLoad;
  baseImageLoad = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      cachedBaseImage = img;
      resolve(img);
    };
    img.onerror = (err) => {
      // Clear the cached promise BEFORE rejecting. Otherwise a 404
      // or decode failure would freeze `baseImageLoad` as a
      // permanently-rejected Promise, and every subsequent call to
      // `loadBaseIcon()` would short-circuit at the cache check
      // above and immediately reject — the favicon overlay would
      // silently never paint again for the lifetime of the page.
      // Clearing here lets the next `applyBadge` retry the fetch
      // (the title prefix and `setAppBadge` keep working in the
      // meantime).
      baseImageLoad = null;
      reject(err);
    };
    // Same path the manifest + push notification icon use.
    img.src = "/icon192.png";
  });
  return baseImageLoad;
}

/**
 * Format the badge text overlay. Counts ≥ 100 collapse to `99+` so
 * three+ digits don't render unreadably small inside the favicon
 * circle. The page title bypasses this and always shows the full
 * number.
 */
function formatBadgeText(count: number): string {
  if (count > 99) return "99+";
  return String(count);
}

/**
 * Paint the base icon + a red-pill overlay with the count, then
 * swap the document's `<link rel="icon">` to a fresh data URL.
 * Catches and swallows decode errors — the title + appBadge fall-
 * backs are sufficient if canvas rendering fails for any reason.
 *
 * `generation` pins the call's place in the queue: a newer call
 * bumps `badgeGeneration` synchronously, and any older paint that
 * resolves later sees `generation < badgeGeneration` and bails
 * before swapping the favicon link.
 */
async function paintFavicon(count: number, generation: number): Promise<void> {
  if (typeof document === "undefined") return;
  try {
    const baseImage = await loadBaseIcon();
    if (generation !== badgeGeneration) return;
    const canvas = document.createElement("canvas");
    canvas.width = FAVICON_SIZE;
    canvas.height = FAVICON_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(baseImage, 0, 0, FAVICON_SIZE, FAVICON_SIZE);

    if (count > 0) {
      const text = formatBadgeText(count);
      // Pill geometry: longer text needs a wider pill so the digits
      // don't crash into the rounded ends.
      const isWide = text.length > 1;
      const pillH = 28;
      const pillW = isWide ? 38 : 28;
      const pillX = FAVICON_SIZE - pillW - 2;
      const pillY = FAVICON_SIZE - pillH - 2;
      ctx.fillStyle = badgeColor();
      ctx.beginPath();
      // `roundRect` is a baseline 2023 API — every browser that
      // supports the Push subscription API also supports it.
      ctx.roundRect(pillX, pillY, pillW, pillH, pillH / 2);
      ctx.fill();

      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 20px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, pillX + pillW / 2, pillY + pillH / 2 + 1);
    }

    // Re-check the generation right before mutating the DOM — the
    // canvas rasterisation above can yield to the event loop on some
    // browsers, so a newer paint may have started in between.
    if (generation !== badgeGeneration) return;
    const dataUrl = canvas.toDataURL("image/png");
    upsertFaviconLink(dataUrl);
  } catch {
    // Silent — the title prefix + appBadge are still updated by the
    // caller, so the user still sees an unread signal.
  }
}

function upsertFaviconLink(href: string): void {
  // Remove any previous canvas-painted favicon so the browser doesn't
  // pick a stale one. We tag our generated link with `data-reflecto-
  // dynamic` so we don't clobber a user-served `<link rel="icon">`
  // that lives in `index.html` for first-paint.
  const existing = document.querySelector(
    "link[rel='icon'][data-reflecto-dynamic='true']"
  ) as HTMLLinkElement | null;
  if (existing) {
    existing.href = href;
    return;
  }
  const link = document.createElement("link");
  link.rel = "icon";
  link.type = "image/png";
  link.href = href;
  link.dataset.reflectoDynamic = "true";
  document.head.appendChild(link);
}

/**
 * Apply the unread-count badge to all three surfaces. Safe to call
 * with `count === 0` — clears every badge surface back to the base
 * state. Also safe under SSR / Node test environments — every DOM
 * touch is guarded.
 */
export function applyBadge(count: number): void {
  // Bump the generation FIRST so any in-flight paint that resolves
  // after this call sees a stale token and bails. Title + appBadge
  // updates below are synchronous so they never need the guard.
  const generation = ++badgeGeneration;
  if (typeof document !== "undefined") {
    document.title = count > 0 ? `(${count}) ${BASE_TITLE}` : BASE_TITLE;
    // Fire-and-forget — the canvas paint is async because of the
    // image decode, but the title + appBadge updates above are
    // synchronous so the user sees a response immediately.
    void paintFavicon(count, generation);
  }
  // App badge (installed PWA / docked taskbar). Browsers without
  // the API just don't expose the methods — no need to feature-
  // detect via try/catch beyond optional chaining.
  if (typeof navigator !== "undefined") {
    if (count > 0) {
      navigator.setAppBadge?.(count).catch(() => {});
    } else {
      navigator.clearAppBadge?.().catch(() => {});
    }
  }
}
